import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Link2, Loader2, Plus, Trash2, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/sourceMeta";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const input = "h-11 min-w-0 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-slate-900 outline-none focus:border-gold-400";

const CalendarLink = ({ cal, reload }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/settings/ics", { url }); toast.success(`Calendar connected: ${data?.events ?? 0} upcoming events`); setUrl(""); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const remove = async () => { if (!window.confirm("Disconnect this calendar?")) return; await api.delete("/settings/ics"); reload(); };
  if (cal?.connected) return (
    <div data-testid="calendar-connected" className="rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-800"><Link2 className="inline w-4 h-4 mr-1 text-gold-300" />Connected to <b>{cal.host}</b>{cal.fetched_at && <span className="text-slate-500"> · synced {fmtTime(cal.fetched_at)}</span>}</p>
        <button data-testid="calendar-disconnect" onClick={remove} className="text-xs font-semibold text-rose-600 hover:underline">Disconnect</button>
      </div>
      {cal.error && <p className="mt-1 text-xs text-rose-600">Last sync failed: {cal.error}</p>}
      <ul className="mt-3 space-y-1">
        {(cal.upcoming ?? []).slice(0, 6).map((e, i) => <li key={i} className="text-xs text-slate-700"><span className="font-mono text-slate-500">{e.date.slice(5)} {e.start}</span> {e.subject}</li>)}
        {!(cal.upcoming ?? []).length && <li className="text-xs text-slate-500">No upcoming events in the next 3 weeks.</li>}
      </ul>
      <p className="mt-2 text-xs text-slate-500"><RefreshCw className="inline w-3 h-3 mr-1" />Refreshes every hour.</p>
    </div>
  );
  return (
    <form data-testid="calendar-link-form" onSubmit={save} className="rounded-xl border border-slate-200 p-4">
      <p className="text-sm font-semibold text-slate-900">Import a calendar</p>
      <p className="text-xs text-slate-600">Outlook: Settings, Calendar, Shared calendars, Publish a calendar, then copy the ICS link. Google: calendar Settings, then "Secret address in iCal format".</p>
      <div className="mt-3 flex gap-2">
        <input data-testid="calendar-url-input" value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://… .ics" className={`${input} flex-1`} />
        <button data-testid="calendar-connect" disabled={busy || !url.trim()} className="inline-flex items-center gap-1.5 h-11 rounded-xl bg-gold-400 px-4 text-sm font-bold text-black disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}Connect</button>
      </div>
    </form>
  );
};

const AddMeeting = ({ reload }) => {
  const [f, setF] = useState({ title: "", start: "09:00", end: "", room: "", repeat: "weekly", weekday: 0, date: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/meetings", { ...f, weekday: f.repeat === "weekly" ? Number(f.weekday) : null, date: f.repeat === "once" ? f.date : null });
      toast.success("Meeting added"); setF({ ...f, title: "", room: "" }); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form data-testid="add-meeting-form" onSubmit={add} className="rounded-xl border border-slate-200 p-4 space-y-3">
      <p className="text-sm font-semibold text-slate-900">Add a meeting by hand</p>
      <input data-testid="meeting-title-input" value={f.title} onChange={set("title")} required maxLength={80} placeholder="e.g. Team stand-up" className={`${input} w-full`} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input data-testid="meeting-start-input" type="time" value={f.start} onChange={set("start")} required className={input} aria-label="Start" />
        <input data-testid="meeting-end-input" type="time" value={f.end} onChange={set("end")} className={input} aria-label="End" />
        <select data-testid="meeting-repeat-select" value={f.repeat} onChange={set("repeat")} className={input}><option value="weekly">Every week</option><option value="once">One-off</option></select>
        {f.repeat === "weekly"
          ? <select data-testid="meeting-weekday-select" value={f.weekday} onChange={set("weekday")} className={input}>{WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select>
          : <input data-testid="meeting-date-input" type="date" value={f.date} onChange={set("date")} required className={input} />}
      </div>
      <div className="flex gap-2">
        <input data-testid="meeting-room-input" value={f.room} onChange={set("room")} maxLength={60} placeholder="Room or link (optional)" className={`${input} flex-1`} />
        <button data-testid="meeting-add-button" className="inline-flex items-center gap-1.5 h-11 rounded-xl bg-brand px-4 text-sm font-bold text-white hover:bg-brand-dark"><Plus className="w-4 h-4" />Add</button>
      </div>
    </form>
  );
};

export const MeetingsCard = () => {
  const [d, setD] = useState(null);
  const load = () => api.get("/meetings").then((r) => setD(r.data ?? null)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/meetings/${id}`).catch(() => {}); load(); };
  const meetings = Array.isArray(d?.meetings) ? d.meetings : [];
  return (
    <article data-testid="connection-card-meetings" className="min-w-0 rounded-2xl bg-surface border border-slate-200 p-5 sm:p-6 lg:col-span-2">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-gold-400/15 text-gold-300"><CalendarDays className="w-5 h-5" /></span>
        <div><h3 className="font-semibold text-slate-900">Work schedule</h3><p className="text-sm text-slate-500">Today's meetings and the next workday appear in every brief.</p></div>
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <CalendarLink cal={d?.calendar} reload={load} />
        <AddMeeting reload={load} />
      </div>
      {meetings.length > 0 && (
        <ul data-testid="meetings-list" className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200">
          {meetings.map((m) => (
            <li key={m.meeting_id} data-testid={`meeting-${m.meeting_id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="w-24 shrink-0 font-mono text-xs leading-tight text-slate-500">{m.repeat === "weekly" ? `Every ${WEEKDAYS[m.weekday]}` : m.date}<br />{m.start}{m.end ? `–${m.end}` : ""}</span>
              <span className="flex-1 min-w-0 truncate text-slate-900">{m.title}{m.room && <span className="text-slate-500"> · {m.room}</span>}</span>
              <button data-testid={`meeting-delete-${m.meeting_id}`} onClick={() => del(m.meeting_id)} aria-label="Delete meeting" className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:bg-rose-600 hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};
